import React from "react";
import { createPortal } from "react-dom";
import { FiActivity, FiClipboard } from "react-icons/fi";
import ModalHeader from "./ModalHeader";

export interface ExerciseLog {
  id: number;
  exercise_id: string;
  exercise_name: string;
  workout_plan_id: number | null;
  workout_plan_name: string;
  completed: boolean;
  date: string;
  date_formatted: string;
}

export interface ExerciseLogs {
  exercise_id: string;
  exercise_name: string;
  total_executions: number;
  logs: ExerciseLog[];
}

interface ExerciseLogsModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly exerciseName: string;
  readonly exerciseLogs: ExerciseLogs | null;
  readonly loading: boolean;
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-20">
      <div className="relative">
        <div className="h-12 w-12 sm:h-16 sm:w-16 animate-spin rounded-full border-4 border-purple-200 border-t-purple-600 dark:border-purple-800 dark:border-t-purple-400"></div>
      </div>
      <p className="mt-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
        Carregando logs...
      </p>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
      <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-5 sm:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
        <FiClipboard
          size={40}
          className="sm:w-12 sm:h-12 text-gray-400 dark:text-gray-600"
        />
      </div>
      <p className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 text-center">
        Nenhum log encontrado
      </p>
      <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
        Não há registros de execução deste exercício no período selecionado
      </p>
    </div>
  );
}

function LogsList({ exerciseLogs }: { readonly exerciseLogs: ExerciseLogs }) {
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Resumo */}
      <div className="rounded-xl bg-gradient-to-r from-purple-50 to-purple-50 border border-purple-100 p-4 sm:p-5 dark:from-purple-900/20 dark:to-purple-900/20 dark:border-purple-800/30">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-600/10 dark:bg-purple-400/10">
            <FiActivity
              className="text-purple-600 dark:text-purple-400"
              size={18}
            />
          </div>
          <div>
            <p className="text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide">
              Total de execuções
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-purple-900 dark:text-purple-100">
              {exerciseLogs.total_executions}
            </p>
          </div>
        </div>
      </div>

      {/* Lista de logs */}
      <div className="space-y-2 sm:space-y-3">
        {exerciseLogs.logs.map((log) => (
          <div
            key={log.id}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 rounded-xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800/50"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <FiActivity
                  className="text-green-600 dark:text-green-400"
                  size={16}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                  {log.workout_plan_name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {log.date_formatted}
                </p>
              </div>
            </div>
            {log.completed && (
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300 whitespace-nowrap flex-shrink-0 self-start sm:self-auto">
                Concluído
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ModalContent({
  loading,
  exerciseLogs,
}: {
  readonly loading: boolean;
  readonly exerciseLogs: ExerciseLogs | null;
}) {
  if (loading) return <LoadingState />;
  if (exerciseLogs && exerciseLogs.logs.length > 0) {
    return <LogsList exerciseLogs={exerciseLogs} />;
  }
  return <EmptyState />;
}

export default function ExerciseLogsModal({
  isOpen,
  onClose,
  exerciseName,
  exerciseLogs,
  loading,
}: ExerciseLogsModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999999] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4 backdrop-blur-sm">
      <div className="relative flex h-[90vh] sm:h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark">
        <ModalHeader exerciseName={exerciseName} onClose={onClose} />

        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-4 sm:p-8 dark:bg-gray-900/20">
          <ModalContent loading={loading} exerciseLogs={exerciseLogs} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
